import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../api';
import './CampaignDetailChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CampaignDetailChart = ({ campaign, customerId, startDate, endDate, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [visibleMetrics, setVisibleMetrics] = useState({
    spending: true,
    impressions: true,
    clicks: true,
    conversions: true,
    cpc: true
  });
  
  // Calcola date di default (ultimi 30 giorni)
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };
  
  const [dateRange, setDateRange] = useState({
    startDate: startDate || getDefaultDates().startDate,
    endDate: endDate || getDefaultDates().endDate
  });

  useEffect(() => {
    loadCampaignData();
  }, [campaign.campaign_id || campaign.id, customerId, dateRange.startDate, dateRange.endDate]);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Usa campaign_id se disponibile, altrimenti campaign.id
      const campaignId = campaign.campaign_id || campaign.id;
      
      const response = await api.get('/merchant/admin/google-ads/campaign-daily', {
        params: {
          campaignId: campaignId,
          customerId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      
      setData(response.data.data);
    } catch (err) {
      setError('Errore nel caricamento dei dati della campagna');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('it-IT').format(number);
  };

  const toggleMetric = (metric) => {
    setVisibleMetrics(prev => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  };

  const getChartData = () => {
    if (!data || !data.dailyData) return null;

    const labels = data.dailyData.map(item => new Date(item.date).toLocaleDateString('it-IT'));
    
    const datasets = [];
    
    if (visibleMetrics.spending) {
      datasets.push({
        label: 'Spesa (€)',
        data: data.dailyData.map(item => item.spending),
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
        yAxisID: 'y-spending'
      });
    }
    
    if (visibleMetrics.impressions) {
      datasets.push({
        label: 'Impressioni',
        data: data.dailyData.map(item => item.impressions),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        yAxisID: 'y-impressions'
      });
    }
    
    if (visibleMetrics.clicks) {
      datasets.push({
        label: 'Clicks',
        data: data.dailyData.map(item => item.clicks),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        yAxisID: 'y-clicks'
      });
    }
    
    if (visibleMetrics.conversions) {
      datasets.push({
        label: 'Conversioni',
        data: data.dailyData.map(item => item.conversions),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.5)',
        yAxisID: 'y-conversions'
      });
    }
    
    if (visibleMetrics.cpc) {
      datasets.push({
        label: 'CPC (€)',
        data: data.dailyData.map(item => item.cpc),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        yAxisID: 'y-cpc'
      });
    }

    return {
      labels,
      datasets
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      'y-spending': {
        type: 'linear',
        display: visibleMetrics.spending,
        position: 'left',
        title: {
          display: true,
          text: 'Spesa (€)'
        }
      },
      'y-impressions': {
        type: 'linear',
        display: visibleMetrics.impressions,
        position: 'right',
        title: {
          display: true,
          text: 'Impressioni'
        }
      },
      'y-clicks': {
        type: 'linear',
        display: visibleMetrics.clicks,
        position: 'right',
        title: {
          display: true,
          text: 'Clicks'
        }
      },
      'y-conversions': {
        type: 'linear',
        display: visibleMetrics.conversions,
        position: 'right',
        title: {
          display: true,
          text: 'Conversioni'
        }
      },
      'y-cpc': {
        type: 'linear',
        display: visibleMetrics.cpc,
        position: 'right',
        title: {
          display: true,
          text: 'CPC (€)'
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Dettaglio Campagna: ${campaign.campaign_name || campaign.name}`
      },
    },
  };

  if (loading) {
    return (
      <div className="campaign-detail-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Caricamento...</h2>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="modal-body">
            <p>Caricamento dati della campagna...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-detail-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h2>Errore</h2>
            <button onClick={onClose} className="close-btn">×</button>
          </div>
          <div className="modal-body">
            <p className="error-message">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-detail-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Dettaglio Campagna: {campaign.campaign_name || campaign.name}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          {/* Selettore Date */}
          <div className="date-selector">
            <h3>Periodo di Analisi</h3>
            <div className="date-inputs">
              <div className="date-input-group">
                <label htmlFor="startDate">Data Inizio:</label>
                <input
                  type="date"
                  id="startDate"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  max={dateRange.endDate}
                />
              </div>
              <div className="date-input-group">
                <label htmlFor="endDate">Data Fine:</label>
                <input
                  type="date"
                  id="endDate"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  min={dateRange.startDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <button 
                className="refresh-btn"
                onClick={loadCampaignData}
                disabled={loading}
              >
                {loading ? 'Caricamento...' : 'Aggiorna Dati'}
              </button>
            </div>
          </div>

          {/* Overview */}
           <div className="averages-section">
             <h3>Overview</h3>
             <div className="averages-grid">
               <div className="average-item">
                 <span className="label">Spesa Totale:</span>
                 <span className="value">{formatCurrency(data.totals.spending)}</span>
               </div>
               <div className="average-item">
                 <span className="label">Impressioni Totali:</span>
                 <span className="value">{formatNumber(data.totals.impressions)}</span>
               </div>
               <div className="average-item">
                 <span className="label">Clicks Totali:</span>
                 <span className="value">{formatNumber(data.totals.clicks)}</span>
               </div>
               <div className="average-item">
                 <span className="label">Conversioni Totali:</span>
                 <span className="value">{data.totals.conversions.toFixed(2)}</span>
               </div>
               <div className="average-item">
                 <span className="label">CPC Medio:</span>
                 <span className="value">{formatCurrency(data.averages.cpc)}</span>
               </div>
             </div>
           </div>

          {/* Pulsanti per toggle metriche */}
          <div className="metrics-toggle">
            <h3>Mostra/Nascondi Metriche</h3>
            <div className="toggle-buttons">
              <button 
                className={`toggle-btn ${visibleMetrics.spending ? 'active' : ''}`}
                onClick={() => toggleMetric('spending')}
              >
                Spesa
              </button>
              <button 
                className={`toggle-btn ${visibleMetrics.impressions ? 'active' : ''}`}
                onClick={() => toggleMetric('impressions')}
              >
                Impressioni
              </button>
              <button 
                className={`toggle-btn ${visibleMetrics.clicks ? 'active' : ''}`}
                onClick={() => toggleMetric('clicks')}
              >
                Clicks
              </button>
              <button 
                className={`toggle-btn ${visibleMetrics.conversions ? 'active' : ''}`}
                onClick={() => toggleMetric('conversions')}
              >
                Conversioni
              </button>
              <button 
                className={`toggle-btn ${visibleMetrics.cpc ? 'active' : ''}`}
                onClick={() => toggleMetric('cpc')}
              >
                CPC
              </button>
            </div>
          </div>

            {/* Grafico */}
           <div className="chart-container">
             {getChartData() && (
               <Line data={getChartData()} options={chartOptions} />
             )}
           </div>

           {/* Pulsante Chiudi */}
           <div className="modal-footer">
             <button 
               className="btn btn-secondary close-modal-btn"
               onClick={onClose}
             >
               Chiudi
             </button>
           </div>
         </div>
       </div>
     </div>
   );
 };

export default CampaignDetailChart; 